/**
 * The signed-in tenant user: identity, permission set and role codes.
 *
 * Permissions are the app's authorisation source of truth on the client. They
 * decide which navigation items exist, which commands the palette offers and
 * which buttons are rendered at all — CLAUDE.md §6 is explicit that an action a
 * user cannot perform should not appear disabled, it should not appear. None of
 * that is a security boundary (the server re-checks every route and again in the
 * service layer); it is what stops the UI offering doors that will not open.
 */

import {
  authLogin,
  authMe,
  authSsoComplete,
  type LoginResult,
  type SSOCompleteRequest,
  type UserDTO,
} from '@erpx/api'
import { DEFAULT_TIME_ZONE, useToast, type ProblemDetails } from '@erpx/ui'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  api,
  applySession,
  clearSession,
  currentAccessToken,
  currentApiGeneration,
  forgetTenantSlug,
  hasStoredSession,
  isSessionEndedError,
  ownRefreshToken,
  publicApi,
  refreshAccessToken,
  rememberTenantSlug,
  revokeRefreshToken,
  sessionEndedProblem,
  sessionExpired,
  tenantSlug,
} from '@/api/client'
import { clearQueryCache } from '@/lib/queryClient'
import { onSessionEnd, runSessionEnd } from '@/lib/sessionReset'

/**
 * The toast queue is module-level state in `@erpx/ui`, which cannot know about
 * this app's session boundaries — so the app registers its reset. A danger
 * toast stays until dismissed ("Could not post INV-2026-000412 for <customer>")
 * and must not still be on screen for the next person.
 */
onSessionEnd(() => {
  useToast().clear()
})

/* ------------------------------------------------- who held this tab before */

/**
 * The principal (`<tenantUuid>:<userId>`) who last held this tab, in
 * sessionStorage — per tab, gone when the tab closes.
 *
 * It decides one thing: whether a sign-in may honour the `?redirect=` the
 * router attached when the session ended. A session that EXPIRED leaves the
 * real principal here, so the same person signs back in and lands where they
 * were. Anyone else is sent home: a redirect is the previous person's
 * location, and following it would put the next person on the previous
 * person's screen (the record id, the filter, the drawer) in the URL.
 *
 * On an explicit sign-out the marker is overwritten with a TOMBSTONE, never
 * removed (CLAUDE.md lesson 11.2): an absent marker is indistinguishable from
 * "nobody has used this tab", and that must not be what a deliberate sign-out
 * leaves behind. The sentinel has no `:`, so it can never equal a principal.
 */
export const LAST_PRINCIPAL_KEY = 'erpx.web.lastPrincipal'
export const SIGNED_OUT_PRINCIPAL = '!signed-out'

function readLastPrincipal(): string | null {
  try {
    return sessionStorage.getItem(LAST_PRINCIPAL_KEY)
  } catch {
    // Blocked storage: no marker means no redirect is ever honoured — the
    // safe direction to fail in.
    return null
  }
}

function writeLastPrincipal(value: string): void {
  try {
    sessionStorage.setItem(LAST_PRINCIPAL_KEY, value)
  } catch {
    // As above: a marker that cannot be written cannot match later.
  }
}

/**
 * How a session ended. `explicit` is somebody pressing "sign out": the next
 * person at this device is presumed to be someone else. `implicit` is
 * everything else — expiry, a refused refresh, a new sign-in on top — where the
 * next person may well be the same one.
 */
type SessionEnd = 'explicit' | 'implicit'

export interface LoginPayload {
  tenantSlug: string
  email: string
  password: string
  totpCode?: string
  deviceName?: string
}

/**
 * A string claim carried by an access token, or `null` when the token is
 * absent, unreadable, or does not carry it as a non-empty string. Read for
 * client-side scoping only — the server verifies the signature and resolves the
 * tenant from it on every request; the client never trusts this value for
 * anything it sends.
 */
function claimFromToken(token: string | null, claim: string): string | null {
  if (token === null) return null
  const payload = token.split('.')[1]
  if (payload === undefined || payload === '') return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const claims: unknown = JSON.parse(atob(padded))
    if (claims === null || typeof claims !== 'object') return null
    const value = (claims as Record<string, unknown>)[claim]
    return typeof value === 'string' && value !== '' ? value : null
  } catch {
    return null
  }
}

/** The tenant UUID (`tid` claim) carried by an access token. */
function tenantFromToken(token: string | null): string | null {
  return claimFromToken(token, 'tid')
}

/**
 * Client-side only — the server never sends it. What `completeSso` rejects
 * with when the session the ticket carries is for a different workspace than
 * the one this tab started the sign-in for (see `completeSso`).
 */
export const SSO_TENANT_MISMATCH = 'ERR_SSO_TENANT_MISMATCH'

function ssoTenantMismatchProblem(): ProblemDetails {
  return {
    type: 'about:blank',
    title: 'This sign-in belongs to a different workspace.',
    status: 401,
    code: SSO_TENANT_MISMATCH,
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserDTO | null>(null)
  /**
   * The signed-in user's tenant. User ids are per-tenant-schema bigints, so
   * user 1 exists in every tenant: anything the client keys per person (nav
   * preferences, saved views) must key on this AND the id — see
   * `composables/useUserScope.ts`. From the access token's `tid` claim; when a
   * token cannot be read (never, in production) it falls back to the slug,
   * which is equally unique and immutable per tenant (§4), so the scope still
   * separates tenants.
   */
  const tenantUuid = ref('')
  const permissions = ref<ReadonlySet<string>>(new Set())
  const roleCodes = ref<readonly string[]>([])
  const rolesHash = ref('')
  const bootstrapped = ref(false)

  /**
   * Whether the last `login()` signed back in the principal who held this tab
   * before (see `LAST_PRINCIPAL_KEY`). The login screen honours `?redirect=`
   * only when this is true.
   */
  const resumedPreviousPrincipal = ref(false)

  /** Collapses concurrent guard runs onto one bootstrap. */
  let inflightBootstrap: Promise<void> | null = null

  /**
   * Bumped on every session boundary. A `/auth/me` that was already in flight
   * when the boundary passed — a `refreshIdentity()` racing a sign-out, say —
   * answers for whoever held the tab before; `loadMe` compares the epoch it
   * started under and discards that answer instead of re-installing the
   * previous person's identity after the teardown.
   */
  let sessionEpoch = 0

  const isAuthenticated = computed(() => user.value !== null)
  const displayName = computed(() => user.value?.name ?? '')
  const slug = computed(() => tenantSlug.value ?? '')

  /**
   * The timezone every instant on screen is rendered in. §5 puts business dates
   * in the tenant timezone with a per-user profile override; the override is
   * what `/auth/me` carries, so an absent one means "use the tenant default",
   * which `EDate` supplies.
   */
  const timeZone = computed(() => user.value?.timezone ?? DEFAULT_TIME_ZONE)
  const locale = computed(() => user.value?.locale ?? 'en')

  /**
   * Whether this account is still carrying a password somebody else chose for
   * it — an administrator creating the user with `credentialMode` set or
   * generate, or a support helper resetting one.
   *
   * The server refuses every tenant route outside the `auth.*` family while it
   * is true (`httpapi.RequireOwnPassword`), so this is not a courtesy: a signed-
   * in user in this state can do exactly one thing, and the router's guard
   * sends them to the screen where they can do it. It travels on `UserDTO`, so
   * it arrives with the login response and with every `/auth/me` — no extra
   * call, and it clears the moment `loadMe` runs after the change.
   */
  const mustChangePassword = computed(() => user.value?.mustChangePassword === true)

  function can(permission: string): boolean {
    return permissions.value.has(permission)
  }

  function canAny(codes: readonly string[]): boolean {
    return codes.some((code) => permissions.value.has(code))
  }

  function reset(): void {
    user.value = null
    tenantUuid.value = ''
    permissions.value = new Set()
    roleCodes.value = []
    rolesHash.value = ''
    resumedPreviousPrincipal.value = false
  }

  /** `<tenantUuid>:<userId>` for whoever is signed in, or `null`. */
  function currentPrincipal(): string | null {
    if (user.value === null || tenantUuid.value === '') return null
    return `${tenantUuid.value}:${user.value.id}`
  }

  /**
   * Drop every piece of client state that belongs to whoever held this tab:
   * the server-state cache (the previous person's figures under keys nobody
   * scoped) and every module-level holder registered with `onSessionEnd`
   * (`lib/sessionReset.ts`). Synchronous, and never throws — `runSessionEnd`
   * isolates each reset.
   */
  function clearClientState(): void {
    sessionEpoch += 1
    clearQueryCache()
    runSessionEnd()
  }

  /**
   * Everything a session boundary must leave behind, in the order that makes
   * it safe: the who-was-here marker (read off the identity before it goes),
   * client state, then the tokens, then the identity. All synchronous, so it
   * is complete before the caller's next statement — which may be a revoke call
   * that hangs or a navigation that is cancelled.
   */
  function endSessionLocally(kind: SessionEnd): void {
    if (kind === 'explicit') {
      writeLastPrincipal(SIGNED_OUT_PRINCIPAL)
      forgetTenantSlug()
      // Somebody who chose to sign out is not told their session expired —
      // nor is whoever sits down next. (Lowering the flag never triggers the
      // watcher's teardown; only raising it does.)
      sessionExpired.value = false
    } else {
      // No identity (a cold load whose refresh failed, a second boundary in a
      // row): leave whatever marker is there rather than erasing who it was.
      const who = currentPrincipal()
      if (who !== null) writeLastPrincipal(who)
    }
    clearClientState()
    clearSession()
    reset()
  }

  /**
   * End whatever session this tab holds because another is about to begin —
   * a sign-in, or an accepted invitation. The local teardown runs first, then
   * the previous session's refresh token is revoked server-side; nothing is
   * awaited, so a revoke that hangs cannot hold up the new session. A tab
   * holding no refresh token sends nothing — and neither does one whose
   * session already ended: the token in shared storage then belongs to
   * another tab's session, which is not this tab's to revoke.
   */
  function endCurrentSession(): void {
    const previous = ownRefreshToken()
    endSessionLocally('implicit')
    bootstrapped.value = true
    void revokeRefreshToken(previous)
  }

  /**
   * A refresh that fails is a session ending without anyone pressing "sign
   * out": `api/client.ts` drops the tokens and raises `sessionExpired`, but it
   * cannot reach the cache, the registries or this store's identity. Without
   * this, the previous person's data stays on screen behind the "session
   * ended" banner — and in memory for whoever signs in next.
   *
   * `flush: 'sync'` so the teardown happens inside the assignment that raised
   * the flag, before the failed request's rejection reaches any caller. The
   * flag itself is left raised: the banner reads it.
   */
  watch(
    sessionExpired,
    (expired) => {
      // An expiry is not a sign-out: the marker keeps the real principal so
      // the same person signing back in resumes where they were.
      if (expired) endSessionLocally('implicit')
    },
    { flush: 'sync' },
  )

  async function loadMe(): Promise<void> {
    const startedUnder = sessionEpoch
    const me = await authMe(api())
    if (startedUnder !== sessionEpoch) return
    const slugValue = tenantSlug.value ?? ''
    tenantUuid.value =
      tenantFromToken(currentAccessToken()) ?? (slugValue === '' ? '' : `slug:${slugValue}`)
    user.value = me.user
    permissions.value = new Set(me.permissions)
    roleCodes.value = me.roleCodes
    rolesHash.value = me.rolesHash
  }

  /**
   * Record the signed-in principal as the one holding this tab. Written once a
   * session is established (not only at teardown), so a session whose end the
   * tab never sees — a reload whose refresh then fails before `/auth/me` —
   * still leaves the right person in the marker.
   */
  function markPrincipal(): void {
    const who = currentPrincipal()
    if (who !== null) writeLastPrincipal(who)
  }

  /**
   * The one sign-in path: password login and SSO completion both run through
   * it, so the boundary crossing, the late-answer guard and the token adoption
   * exist exactly once.
   *
   * `resolveSlug` names the workspace the new session is for. It runs after
   * the response and BEFORE anything of it is adopted; if it throws, the
   * freshly minted session is revoked and nothing of it reaches the tab.
   */
  async function establishSession(
    request: () => Promise<LoginResult>,
    resolveSlug: (result: LoginResult) => string,
  ): Promise<void> {
    // A sign-in is a session boundary even when no sign-out preceded it, and
    // it is crossed BEFORE the request: a sign-in that hangs or is refused
    // must not leave whoever held the tab live in it (their cache, identity,
    // tokens), and their server session is revoked rather than left to run
    // out its 30 days. A session that ended by refresh failure
    // (`sessionExpired`) has already been torn down; this is then a no-op
    // that sends nothing.
    endCurrentSession()

    // Another boundary can pass while the sign-in is in flight: an invitation
    // accepted in this tab, a second sign-in submitted on top of this one. The
    // tab then belongs to that session, and this answer must not be laid over
    // it. Its freshly minted refresh token is revoked rather than dropped —
    // dropped, it would be a live session nobody holds, for 30 days.
    const generation = currentApiGeneration()
    const result = await request()
    if (generation !== currentApiGeneration()) {
      void revokeRefreshToken(result.refreshToken)
      throw sessionEndedProblem()
    }
    let slugValue: string
    try {
      slugValue = resolveSlug(result)
    } catch (error) {
      // Refused: the same reasoning as the late answer above — a session
      // nobody adopts is revoked, never left to run.
      void revokeRefreshToken(result.refreshToken)
      throw error
    }
    // Again after the await: nothing of the old session can have come back
    // in the meantime (every holder registered with `onSessionEnd`), and
    // `reset()` makes sure that, if this session's `/auth/me` then fails, no
    // identity at all is left standing on the new tokens.
    clearClientState()
    reset()
    applySession(result, slugValue)
    rememberTenantSlug(slugValue)
    // Read the marker before this session overwrites it.
    const previous = readLastPrincipal()
    await loadMe()
    const who = currentPrincipal()
    resumedPreviousPrincipal.value = who !== null && previous === who
    markPrincipal()
    bootstrapped.value = true
  }

  async function login(payload: LoginPayload): Promise<void> {
    // Built key by key: under `exactOptionalPropertyTypes` an explicit
    // `undefined` is not the same as an absent optional property.
    const body = {
      tenantSlug: payload.tenantSlug,
      email: payload.email,
      password: payload.password,
      ...(payload.totpCode === undefined || payload.totpCode === ''
        ? {}
        : { totpCode: payload.totpCode }),
      ...(payload.deviceName === undefined ? {} : { deviceName: payload.deviceName }),
    }
    await establishSession(
      () => authLogin(publicApi(), body),
      () => payload.tenantSlug,
    )
  }

  /**
   * Finish a single sign-on: exchange the one-time ticket the API's own
   * `/auth/sso/callback` handed the web app for a session, through the same
   * path as `login()`.
   *
   * The response carries no workspace, so the session's slug is read from the
   * access token's `slug` claim — the one the server minted (§4: the tenant is
   * a claim we issued, never client input). It must equal `expectedTenantSlug`,
   * the workspace THIS tab started the sign-in for (`modules/auth/ssoFlow.ts`).
   * A ticket for any other workspace is somebody else's sign-in planted on this
   * tab (login CSRF): it is refused, and the session it carried is revoked
   * rather than adopted.
   */
  async function completeSso(
    ticket: string,
    expectedTenantSlug: string,
    browserNonce: string,
  ): Promise<void> {
    // `browserNonce` binds the ticket to this tab server-side (identity's
    // CompleteSSOTicket refuses and burns a ticket presented without the nonce
    // its flow started with). Typed as an extension of the generated request
    // until the client is regenerated with the field.
    const body: SSOCompleteRequest & { browserNonce: string } = { ticket, browserNonce }
    await establishSession(
      () => authSsoComplete(publicApi(), body),
      (result) => {
        const issued = claimFromToken(result.accessToken, 'slug')
        if (issued === null || issued !== expectedTenantSlug) throw ssoTenantMismatchProblem()
        return issued
      },
    )
  }

  /**
   * Sign out: end the session locally FIRST, then revoke it server-side.
   *
   * The refresh token is read before the teardown removes it — it is the
   * credential `POST /auth/revoke` takes, and unlike the 15-minute access token
   * it is still valid in a tab that sat idle before somebody pressed "sign
   * out". (Revoking with the access token, as `POST /auth/logout` does, failed
   * with a 401 on exactly that tab and left the server session alive for 30
   * days; `/auth/logout` is not called at all now — the revoke covers it.)
   *
   * The teardown then runs before anything is awaited: a revoke that hangs (or
   * a navigation the user cancels) must not leave the previous person's cache,
   * identity or tokens live in this tab. It is explicit, so it tombstones the
   * principal marker and forgets the remembered workspace — the next person at
   * this device is presumed to be somebody else.
   */
  async function logout(): Promise<void> {
    // This tab's own session's token — never whatever another tab has since
    // written to shared storage (`ownRefreshToken`).
    const refreshToken = ownRefreshToken()
    endSessionLocally('explicit')
    bootstrapped.value = true
    // Never throws: a revoke that fails has still ended the session locally,
    // and the server expires the token on its own clock.
    await revokeRefreshToken(refreshToken)
  }

  /**
   * Restore a session on first load: exchange the stored refresh token for an
   * access token, then read the identity and permission set back from the API.
   */
  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return
    inflightBootstrap ??= (async () => {
      try {
        // A cold load has no access token in memory (it lives 15 minutes and is
        // never persisted), so the stored refresh token is exchanged for one.
        // An access token that *is* already in hand — a session established in
        // this same page life — needs no exchange.
        if (currentAccessToken() === null) {
          if (!hasStoredSession()) return
          const refreshed = await refreshAccessToken()
          if (!refreshed) return
        }
        await loadMe()
        markPrincipal()
      } catch (error) {
        // The session this bootstrap was restoring ended while it ran (another
        // tab signed out, or a sign-in here began): the boundary that ended it
        // already tore it down, and a second teardown now would end whatever
        // session came after it.
        if (isSessionEndedError(error)) return
        // A broken session is indistinguishable from no session, and the guard
        // treats both the same way: send the user to the login screen.
        endSessionLocally('implicit')
      } finally {
        bootstrapped.value = true
      }
    })().finally(() => {
      inflightBootstrap = null
    })
    return inflightBootstrap
  }

  /** Re-read `/auth/me` after a profile or role change. */
  async function refreshIdentity(): Promise<void> {
    if (!isAuthenticated.value) return
    await loadMe()
  }

  /**
   * Adopt a session established outside `login()` — accepting an invitation
   * returns a full token pair, and there is no reason to send a new colleague
   * back to the sign-in form to retype the password they just chose.
   *
   * The caller ends the tab's previous session with `endCurrentSession()`
   * BEFORE its own request (see `AcceptInviteView`), which is what revokes it
   * server-side; this re-crosses the boundary once the new tokens are in.
   */
  async function hydrate(): Promise<void> {
    // A new identity in this tab, the same boundary as `login()` — identity
    // included, so a `/auth/me` that fails cannot leave whoever held the tab
    // before signed in on top of the new person's tokens.
    clearClientState()
    reset()
    await loadMe()
    markPrincipal()
    bootstrapped.value = true
  }

  /**
   * End the session in this tab without a server round trip. Counted as an
   * explicit sign-out (tombstoned marker, forgotten workspace): it is only
   * ever a deliberate act, and the conservative reading is the safe one.
   */
  function signOutLocally(): void {
    endSessionLocally('explicit')
  }

  return {
    user,
    tenantUuid,
    permissions,
    roleCodes,
    rolesHash,
    bootstrapped,
    resumedPreviousPrincipal,
    sessionExpired,
    isAuthenticated,
    displayName,
    slug,
    timeZone,
    locale,
    mustChangePassword,
    can,
    canAny,
    login,
    completeSso,
    logout,
    bootstrap,
    refreshIdentity,
    hydrate,
    endCurrentSession,
    signOutLocally,
  }
})
